import { useState } from 'react'
import { X, Crown, Check } from 'lucide-react'
import { Button, Input } from '../UI'

const plans = [
  { id: 'free', name: 'Free', description: 'No subscription', credits: 0 },
  { id: 'basic', name: 'Basic', description: '500 credits/month', credits: 500 },
  { id: 'pro', name: 'Pro', description: '2,000 credits/month', credits: 2000 },
]

const PlanOverrideModal = ({ isOpen, onClose, onSubmit, userName, currentPlan }) => {
  const [selectedPlan, setSelectedPlan] = useState(currentPlan || 'free')
  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (selectedPlan === currentPlan) {
      setError('Selected plan is the same as current plan')
      return
    }

    setLoading(true)
    setError(null)

    try {
      await onSubmit(selectedPlan, note)
      onClose()
      setNote('')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-dark-900 rounded-xl border border-dark-700 w-full max-w-md p-6">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-dark-400 hover:text-dark-200 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-accent-500/20 rounded-lg">
            <Crown className="w-5 h-5 text-accent-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Override Plan</h3>
            <p className="text-sm text-dark-400">{userName}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Plan Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-dark-300 mb-2">Select Plan</label>
            {plans.map((plan) => (
              <button
                key={plan.id}
                type="button"
                onClick={() => setSelectedPlan(plan.id)}
                className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
                  selectedPlan === plan.id
                    ? 'bg-accent-500/10 border-accent-500/30'
                    : 'bg-dark-800 border-dark-700 hover:border-dark-600'
                }`}
              >
                <div className="flex items-center gap-3">
                  {plan.id === 'pro' && <Crown className="w-4 h-4 text-accent-400" />}
                  <div className="text-left">
                    <p className="font-medium">{plan.name}</p>
                    <p className="text-sm text-dark-400">{plan.description}</p>
                  </div>
                </div>
                {selectedPlan === plan.id && (
                  <Check className="w-5 h-5 text-accent-400" />
                )}
                {currentPlan === plan.id && selectedPlan !== plan.id && (
                  <span className="text-xs text-dark-400">Current</span>
                )}
              </button>
            ))}
          </div>

          {/* Note Input */}
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Note (optional)</label>
            <Input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Reason for plan change"
            />
          </div>

          {/* Warning for downgrades */}
          {selectedPlan !== currentPlan && (
            <div className={`p-3 rounded-lg text-sm ${
              plans.find(p => p.id === selectedPlan)?.credits < plans.find(p => p.id === currentPlan)?.credits
                ? 'bg-yellow-500/10 border border-yellow-500/20 text-yellow-400'
                : 'bg-green-500/10 border border-green-500/20 text-green-400'
            }`}>
              {plans.find(p => p.id === selectedPlan)?.credits < plans.find(p => p.id === currentPlan)?.credits
                ? 'This is a downgrade. Monthly credit allocation will decrease.'
                : 'This is an upgrade. User will receive additional credits.'}
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={loading || selectedPlan === currentPlan}>
              {loading ? 'Saving...' : 'Override Plan'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PlanOverrideModal
